import React from 'react';
import {
  Avatar,
  Button,
  Checkbox,
  InputAdornment,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Product } from '@domain/product/models/Product';
import type { PerProductLinkFormState } from './supplierLinkTypes';
import { currencySymbol, summarizeMoq } from './supplierLinkFormUtils';

export interface ProductLinkRowProps {
  product: Product;
  row: PerProductLinkFormState;
  currencyCode: string;
  onOpenMoqMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onPurchasePriceChange: (value: string) => void;
  selected?: boolean;
  onToggleSelect?: () => void;
}

const ProductLinkRow: React.FC<ProductLinkRowProps> = ({
  product,
  row,
  currencyCode,
  onOpenMoqMenu,
  onPurchasePriceChange,
  selected = false,
  onToggleSelect,
}) => {
  const moqSummary = summarizeMoq(row, currencyCode);

  return (
    <TableRow
      sx={{
        '&:last-child td': { borderBottom: 0 },
      }}
    >
      <TableCell
        padding="checkbox"
        sx={{ verticalAlign: 'middle', pl: 1 }}
        onClick={e => e.stopPropagation()}
      >
        <Checkbox
          checked={selected}
          onChange={() => onToggleSelect?.()}
          inputProps={{ 'aria-label': `Select ${product.title || product.ean || 'product'}` }}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'middle', p: 4 }}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Avatar
            src={product.image}
            variant="rounded"
            sx={{ width: 40, height: 40, border: 1, borderColor: 'divider' }}
          />
          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              display: '-webkit-box',
            }}
          >
            {product.title || product.ean || 'Untitled'}
          </Typography>
        </Stack>
      </TableCell>

      <TableCell align="right" sx={{ verticalAlign: 'middle', p: 4 }}>
        <Button
          onClick={e => {
            e.stopPropagation();
            onOpenMoqMenu(e);
          }}
          sx={{ whiteSpace: 'nowrap', maxWidth: 220, textOverflow: 'ellipsis', overflow: 'hidden' }}
          variant="text"
          color="primary"
          size="small"
        >
          {moqSummary || 'Add MOQ'}
        </Button>
      </TableCell>

      <TableCell align="right" sx={{ verticalAlign: 'middle', p: 4 }}>
        <TextField
          type="number"
          value={row.purchasePrice}
          onChange={e => onPurchasePriceChange(e.target.value)}
          onClick={e => e.stopPropagation()}
          inputProps={{ step: 0.01, min: 0 }}
          size="small"
          placeholder="0"
          sx={{ width: 130, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography variant="body2" color="text.secondary">
                  {currencySymbol(currencyCode)}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </TableCell>
    </TableRow>
  );
};

export default ProductLinkRow;
